import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ComponentProps } from 'react';

import { cn } from '@/shared/lib/utils';

export const Dialogue = DialogPrimitive.Root;
export const DeclencheurDialogue = DialogPrimitive.Trigger;
export const FermetureDialogue = DialogPrimitive.Close;

/**
 * Contenu d'une boite de dialogue.
 *
 * Sans animation d'entree (§0.1). Radix pose bien ses attributs `data-state`, mais aucune
 * transition n'y est accrochee : sur un outil ou l'on ouvre des dizaines de boites par
 * jour, une apparition de 200 ms devient une attente de 200 ms.
 */
export function ContenuDialogue({ className, children, ...reste }: ComponentProps<typeof DialogPrimitive.Content>) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60" />

      <DialogPrimitive.Content
        className={cn(
          'fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2',
          'rounded-[--radius-astro] border border-bordure bg-fond-eleve shadow-xl',
          'max-h-[85dvh] overflow-y-auto',
          className,
        )}
        {...reste}
      >
        {children}

        <DialogPrimitive.Close
          className={cn(
            'absolute right-3 top-3 rounded-[--radius-astro] p-1 text-texte-faible',
            'transition-colors duration-100 hover:bg-surface-haute hover:text-texte',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
          )}
          aria-label="Fermer"
        >
          <X className="size-3.5" />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export function EnTeteDialogue({ className, ...reste }: ComponentProps<'div'>) {
  return <div className={cn('space-y-1 border-b border-bordure p-4 pr-10', className)} {...reste} />;
}

export function TitreDialogue({ className, ...reste }: ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn('text-base font-semibold text-texte', className)}
      {...reste}
    />
  );
}

export function DescriptionDialogue({
  className,
  ...reste
}: ComponentProps<typeof DialogPrimitive.Description>) {
  return <DialogPrimitive.Description className={cn('text-texte-doux', className)} {...reste} />;
}

export function CorpsDialogue({ className, ...reste }: ComponentProps<'div'>) {
  return <div className={cn('p-4', className)} {...reste} />;
}

export function PiedDialogue({ className, ...reste }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex justify-end gap-2 border-t border-bordure p-4', className)}
      {...reste}
    />
  );
}
